import { useState, useEffect } from 'react';
import { Bell, TrendingDown, Calendar, CreditCard, TrendingUp, Mail, Send } from 'lucide-react';
import { useUser } from '@hooks/useUser';
import Card from '@components/common/Card';
import Button from '@components/common/Button';

const notificationGroups = [
  {
    group: 'Transações',
    icon: TrendingDown,
    items: [
      { key: 'largePurchase', label: 'Compra acima de um limite', description: 'Alertas para transações acima de R$ 500' },
      { key: 'balanceLow', label: 'Saldo baixo', description: 'Quando o saldo da conta cair abaixo de R$ 100' },
    ],
  },
  {
    group: 'Vencimentos',
    icon: Calendar,
    items: [
      { key: 'recurringDue', label: 'Recorrências a vencer', description: 'Lembrete 3 dias antes do vencimento' },
      { key: 'installmentDue', label: 'Parcelas a vencer', description: 'Lembrete 2 dias antes do vencimento' },
    ],
  },
  {
    group: 'Metas e Orçamento',
    icon: TrendingUp,
    items: [
      { key: 'budgetAlert', label: 'Orçamento comprometido', description: 'Quando atingir 80% do orçamento mensal' },
      { key: 'goalReached', label: 'Meta atingida', description: 'Ao completar uma meta de investimento' },
    ],
  },
  {
    group: 'E-mail',
    icon: Mail,
    items: [
      { key: 'dailyDigest', label: 'Resumo diário', description: 'Resumo das movimentações do dia todo dia às 20h' },
      { key: 'weeklyDigest', label: 'Resumo semanal', description: 'Relatório da semana toda segunda-feira' },
      { key: 'monthlyReport', label: 'Relatório mensal', description: 'Fechamento do mês por e-mail no dia 1' },
    ],
  },
];

const Toggle = ({ checked, onChange }) => (
  <button
    role="switch"
    aria-checked={checked}
    onClick={() => onChange(!checked)}
    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
      checked ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-700'
    }`}
  >
    <span
      className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${
        checked ? 'translate-x-6' : 'translate-x-1'
      }`}
    />
  </button>
);

const Notifications = () => {
  const { profile, updatePreferences, isSavingPrefs, sendDigest, isSendingDigest } = useUser();

  const [prefs, setPrefs] = useState({
    largePurchase: true,
    balanceLow: true,
    recurringDue: true,
    installmentDue: true,
    budgetAlert: true,
    goalReached: true,
    dailyDigest: false,
    weeklyDigest: false,
    monthlyReport: true,
  });
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    if (profile?.preferences?.notificationPrefs) {
      setPrefs(prev => ({ ...prev, ...profile.preferences.notificationPrefs }));
    }
  }, [profile]);

  const handleToggle = (key, value) => {
    setPrefs(prev => ({ ...prev, [key]: value }));
    setIsDirty(true);
  };

  const handleSave = async () => {
    await updatePreferences({ notificationPrefs: prefs });
    setIsDirty(false);
  };

  return (
    <div className="space-y-6">
      {notificationGroups.map(({ group, icon: Icon, items }) => (
        <Card key={group} title={group}>
          <div className="space-y-0">
            {items.map((item, idx) => (
              <div
                key={item.key}
                className={`flex items-center justify-between py-4 ${
                  idx > 0 ? 'border-t border-gray-100 dark:border-gray-700' : ''
                }`}
              >
                <div className="flex items-start gap-3 flex-1 mr-4">
                  <div className="w-8 h-8 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Icon size={16} className="text-gray-500 dark:text-gray-400" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {item.label}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      {item.description}
                    </p>
                  </div>
                </div>
                <Toggle
                  checked={prefs[item.key]}
                  onChange={(value) => handleToggle(item.key, value)}
                />
              </div>
            ))}
          </div>
        </Card>
      ))}

      <div className="flex items-center justify-between">
        <Button
          variant="secondary"
          leftIcon={<Send size={16} />}
          onClick={() => sendDigest()}
          isLoading={isSendingDigest}
        >
          Enviar resumo agora
        </Button>
        <Button
          onClick={handleSave}
          isLoading={isSavingPrefs}
          disabled={!isDirty}
        >
          Salvar Preferências
        </Button>
      </div>
    </div>
  );
};

export default Notifications;
